import { useCallback } from 'react'

import { useMutation } from '@apollo/client'
import { pick } from 'lodash-es'

import { LOGOUT_CUSTOMER, SIGN_UP_CUSTOMER } from 'Const'
import { SignUpStates } from 'Hooks/checkout'
import { Customer } from 'Types'
import { sendGTMAccountCreatedEvent } from 'Utils/analytics/gtmEvents'
import { noty } from 'Utils/index'

type Args = {
  nextStep: (payload: Record<string, unknown>) => void
  onLogoutClearClient: () => void
  requiresSmsOtp: boolean
  resetToStep: () => void
  setState: (state: SignUpStates) => void
}

function useCustomerContactMutationsImpl({
  requiresSmsOtp,
  nextStep,
  setState,
  resetToStep,
  onLogoutClearClient,
}: Args) {
  const [signUp, { loading: signUpLoading }] = useMutation(SIGN_UP_CUSTOMER, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          currentCustomer() {
            if (data.signUpCustomer.customer) {
              return {
                ...(data.signUpCustomer.customer as Customer),
                __typename: 'Customer',
              }
            }
          },
        },
      })
    },
    onCompleted: (data) => {
      if (data.signUpCustomer.state == SignUpStates.COMPLETED) {
        const customer = data.signUpCustomer.customer
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
        sendGTMAccountCreatedEvent(customer)
      } else if (data.signUpCustomer.state == SignUpStates.CODE && requiresSmsOtp) {
        noty('Enter code from SMS', 'success')
      }
      setState(data.signUpCustomer.state)
    },
  })

  const [logout] = useMutation(LOGOUT_CUSTOMER, {
    update(cache) {
      cache.modify({
        fields: {
          currentCustomer() {
            return null
          },
        },
      })
    },
    onCompleted: () => {
      resetToStep()
      setState(SignUpStates.PHONE)
      onLogoutClearClient()
    },
  })

  const handleLogout = useCallback(() => {
    void logout()
  }, [logout])

  return { handleLogout, signUp, signUpLoading }
}

export type CustomerContactMutations = ReturnType<typeof useCustomerContactMutationsImpl>

export function useCustomerContactMutations(args: Args): CustomerContactMutations {
  return useCustomerContactMutationsImpl(args)
}
